import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ArrowLeft, MapPin, Star, MessageSquare, Info, FolderLock, FolderOpen, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { LocationMap } from "@/components/MapPin";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

const Field = ({ label, value }) =>
  value ? (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300/70">{label}</div>
      <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap mt-0.5">{value}</p>
    </div>
  ) : null;

export default function WorkerParticipantDetail() {
  const { name } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState({ internal: [], external: [] });

  useEffect(() => {
    api.workerParticipant(name)
      .then(setP)
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load participant."))
      .finally(() => setLoading(false));
    api.workerParticipantDocuments(name).then((d) => setDocs(d || { internal: [], external: [] })).catch(() => {});
  }, [name]);

  if (loading)
    return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin text-[#00c86e]" /></div>;
  if (!p)
    return <div className="px-4 pt-6 text-slate-400 text-sm">Participant unavailable.</div>;

  return (
    <div className="px-4 pt-4 pb-6" data-testid="worker-participant-detail">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-slate-400 text-xs mb-3" data-testid="worker-participant-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-white tracking-tight">{p.display_name}</h1>
          {p.is_preferred && <Star className="h-4 w-4 text-amber-400 fill-amber-400" />}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400">
          {p.pronouns && <span>{p.pronouns}</span>}
          {p.participant_age && <span>{p.participant_age} yrs</span>}
        </div>
        {p.primary_address && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-300"><MapPin className="h-3.5 w-3.5 text-[#00c86e]" /> {p.primary_address}</div>
        )}
        {p.address_latitude && p.address_longitude && (
          <LocationMap lat={p.address_latitude} lng={p.address_longitude} label={p.display_name} height={150} className="mt-3" />
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 space-y-4">
        <Field label="Briefing" value={p.briefing_on_client} />
        <Field label="Get to know me" value={p.get_to_know_me} />
        <Field label="Hobbies" value={p.hobbies} />
        <Field label="Communication" value={p.communication_requirements} />
        <Field label="Living arrangements" value={p.living_arrangements} />
        <Field label="Emergency instructions" value={p.emergency_instructions} />
        <Field label="If contact fails at shift start" value={p.procedures_for_unsuccessful_contact_at_shift_start} />
      </div>

      {/* Documents — two clearly separated categories, each opens its own viewer screen */}
      <div className="mt-4 space-y-2.5">
        <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-300/80 px-1">Documents</div>
        <button
          onClick={() => nav(`/worker/participant/${encodeURIComponent(name)}/documents/internal`)}
          className="w-full text-left rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-3 active:bg-white/10"
          data-testid="worker-docs-internal">
          <div className="h-10 w-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
            <FolderLock className="h-5 w-5 text-[#00c86e]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-semibold">Internal Documents for Support Workers</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Care plans &amp; signed documents prepared for support workers · {docs.internal.length}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
        </button>
        <button
          onClick={() => nav(`/worker/participant/${encodeURIComponent(name)}/documents/external`)}
          className="w-full text-left rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-3 active:bg-white/10"
          data-testid="worker-docs-external">
          <div className="h-10 w-10 rounded-xl bg-sky-500/15 flex items-center justify-center shrink-0">
            <FolderOpen className="h-5 w-5 text-sky-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-semibold">External Documents</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Documents from other systems shared with you (e.g. PBSP) · {docs.external.length}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
        </button>
      </div>

      {/* Shift note history */}
      <div className="mt-4">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-300/80 mb-2 px-1">
          <MessageSquare className="h-3.5 w-3.5" /> Shift Notes History
          {p.notes_scope === "own" && <span className="text-[9px] font-medium text-slate-500 normal-case tracking-normal">(your notes)</span>}
        </div>
        {(!p.notes || p.notes.length === 0) ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-xs text-slate-500 flex items-center gap-2" data-testid="worker-participant-no-notes">
            <Info className="h-3.5 w-3.5" /> No shift notes recorded yet.
          </div>
        ) : (
          <div className="space-y-2.5">
            {p.notes.map((n, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`worker-participant-note-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white font-medium">{n.support_worker_name || "Support Worker"}</div>
                  <span className="text-[10px] text-slate-500">{String(n.shift_date || "").slice(0, 10)}</span>
                </div>
                {n.urgent_notes && (
                  <div className="mt-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300/80">Important</div>
                    <p className="text-xs text-amber-200/90 leading-relaxed whitespace-pre-wrap">{n.urgent_notes}</p>
                  </div>
                )}
                <div className="mt-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300/70">Events</div>
                  <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{n.events || "—"}</p>
                </div>
                {n.mood && (
                  <div className="mt-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300/70">Mood</div>
                    <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{n.mood}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
